import type { DashboardGridSection } from './grid/DashboardGrid'
import { WIDGET_KEY, type WidgetProps } from './widgets/types'
import { WidgetErrorBoundary } from './widgets/WidgetErrorBoundary'
import { CreditValueByJurisdictionFyWidget } from './widgets/CreditValueByJurisdictionFyWidget'
import { OutcomeVarianceWidget } from './widgets/OutcomeVarianceWidget'
import { MonetizationOutcomesWidget } from './widgets/MonetizationOutcomesWidget'
import { FilingsByStatusWidget } from './widgets/FilingsByStatusWidget'
import { SpendTrendWidget } from './widgets/SpendTrendWidget'
import { ReadinessComingSoonWidget } from './widgets/ReadinessComingSoonWidget'
import { PortfolioPipelineFunnelWidget } from './widgets/PortfolioPipelineFunnelWidget'
import { CreditsByJurisdictionStatusWidget } from './widgets/CreditsByJurisdictionStatusWidget'
import { TotalCertifiedValueKpiWidget } from './dashboards/portfolio/TotalCertifiedValueKpiWidget'
import { StatusCountRowWidget } from './dashboards/portfolio/StatusCountRowWidget'
import type { DashboardFilter } from '../../types/insights'

export interface DashboardRegistryContext {
  filter: DashboardFilter
  widgetProps: (widgetKey: string) => WidgetProps
  skipRefetchFor: (widgetKey: string) => boolean
}

/**
 * The declarative widget registry behind `/insights/dashboards` (IB-311 Slice 1 — design-system.md
 * §5: "widget key -> column span + min size + section", config not hardcoded JSX). `DashboardScreen`
 * renders `<DashboardGrid sections={buildDashboardSections(ctx)} />` and nothing else for its tile
 * layout — adding, resizing, regrouping, or reordering a tile is a change to the data below, never
 * to the screen component.
 *
 * Layout at a glance:
 *   - **hero** (untitled, top): 3 compact KPI stat tiles — total certified value, claimed-vs-approved
 *     variance, and count of filings by status — "the numbers that matter most" (IB-311 BUILD §1).
 *   - **Credit performance**: the full credit-value-by-jurisdiction/FY bar (primary, 8/12 wide on
 *     desktop) + the qualified-spend trend line (4/12).
 *   - **Portfolio pipeline & jurisdiction mix**: the lifecycle-stage funnel + the jurisdiction/status
 *     bar, 6/12 each.
 *   - **Filings & monetization**: filings-by-status funnel + monetization-outcomes donut, 6/12 each.
 *   - **Readiness**: the honest "coming soon" tile (no `evidence.api` read port yet — IB-146).
 *
 * Every `key` doubles as the tile's `WIDGET_KEY` for `widgetProps`/cross-filter-origin bookkeeping,
 * EXCEPT the two hero tiles built from a SECOND view over an existing key
 * (`TotalCertifiedValueKpiWidget`/`StatusCountRowWidget`, reused as-is from `dashboards/portfolio` —
 * same "second view over the same widget key, own independent fetch" pattern that module's own
 * `TopJurisdictionsWidget`/`CreditsByJurisdictionStatusWidget` already established) — those get a
 * registry-local key (`hero-*`) purely so `DashboardGrid` has a unique React key per cell; the
 * `WIDGET_KEY` passed into `widgetProps(...)` inside their `render()` is still the real widget key.
 */
export function buildDashboardSections(ctx: DashboardRegistryContext): DashboardGridSection[] {
  const { widgetProps, filter, skipRefetchFor } = ctx

  return [
    {
      id: 'hero',
      variant: 'hero',
      hiddenHeadingLabel: 'Key metrics',
      widgets: [
        {
          key: 'hero-total-certified-value',
          span: { base: 12, md: 4, lg: 4 },
          minHeight: 140,
          render: () => (
            <WidgetErrorBoundary title="Total certified value" icon="dollar-sign">
              <TotalCertifiedValueKpiWidget {...widgetProps(WIDGET_KEY.creditValueByJurisdictionFy)} />
            </WidgetErrorBoundary>
          ),
        },
        {
          key: WIDGET_KEY.outcomeVariance,
          span: { base: 12, md: 4, lg: 4 },
          minHeight: 140,
          render: () => (
            <WidgetErrorBoundary title="Claimed vs. approved variance" icon="activity">
              <OutcomeVarianceWidget {...widgetProps(WIDGET_KEY.outcomeVariance)} />
            </WidgetErrorBoundary>
          ),
        },
        {
          key: 'hero-count-by-status',
          span: { base: 12, md: 4, lg: 4 },
          minHeight: 140,
          render: () => (
            <WidgetErrorBoundary title="Count by status" icon="briefcase">
              <StatusCountRowWidget {...widgetProps(WIDGET_KEY.filingsByStatus)} />
            </WidgetErrorBoundary>
          ),
        },
      ],
    },
    {
      id: 'credit-performance',
      title: 'Credit performance',
      description: 'Certified value across jurisdictions and fiscal years, and the qualified-spend trend behind it.',
      widgets: [
        {
          key: WIDGET_KEY.creditValueByJurisdictionFy,
          span: { base: 12, lg: 8 },
          minHeight: 360,
          render: () => (
            <WidgetErrorBoundary title="Credit value by jurisdiction / FY" icon="bar-chart">
              <CreditValueByJurisdictionFyWidget {...widgetProps(WIDGET_KEY.creditValueByJurisdictionFy)} />
            </WidgetErrorBoundary>
          ),
        },
        {
          key: WIDGET_KEY.qreSpendTrend,
          span: { base: 12, lg: 4 },
          minHeight: 360,
          render: () => (
            <WidgetErrorBoundary title="Qualified-spend trend" icon="activity">
              <SpendTrendWidget {...widgetProps(WIDGET_KEY.qreSpendTrend)} />
            </WidgetErrorBoundary>
          ),
        },
      ],
    },
    {
      id: 'pipeline-and-jurisdictions',
      title: 'Portfolio pipeline & jurisdiction mix',
      widgets: [
        {
          key: WIDGET_KEY.portfolioPipelineFunnel,
          span: { base: 12, lg: 6 },
          minHeight: 340,
          render: () => (
            <WidgetErrorBoundary title="Portfolio pipeline by lifecycle stage" icon="activity">
              <PortfolioPipelineFunnelWidget {...widgetProps(WIDGET_KEY.portfolioPipelineFunnel)} />
            </WidgetErrorBoundary>
          ),
        },
        {
          key: WIDGET_KEY.creditsByJurisdictionStatus,
          span: { base: 12, lg: 6 },
          minHeight: 340,
          render: () => (
            <WidgetErrorBoundary title="Credits by jurisdiction & status" icon="dollar-sign">
              <CreditsByJurisdictionStatusWidget {...widgetProps(WIDGET_KEY.creditsByJurisdictionStatus)} />
            </WidgetErrorBoundary>
          ),
        },
      ],
    },
    {
      id: 'filings-and-monetization',
      title: 'Filings & monetization',
      widgets: [
        {
          key: WIDGET_KEY.filingsByStatus,
          span: { base: 12, lg: 6 },
          minHeight: 340,
          render: () => (
            <WidgetErrorBoundary title="Filings by status" icon="briefcase">
              <FilingsByStatusWidget {...widgetProps(WIDGET_KEY.filingsByStatus)} />
            </WidgetErrorBoundary>
          ),
        },
        {
          key: WIDGET_KEY.monetizationOutcomes,
          span: { base: 12, lg: 6 },
          minHeight: 340,
          render: () => (
            <WidgetErrorBoundary title="Monetization outcomes" icon="landmark">
              <MonetizationOutcomesWidget {...widgetProps(WIDGET_KEY.monetizationOutcomes)} />
            </WidgetErrorBoundary>
          ),
        },
      ],
    },
    {
      // No `title` -- the section's sole widget below already renders its own "Readiness" card
      // heading (`WidgetErrorBoundary title="Readiness"`); a section-level `<h2>Readiness</h2>`
      // wrapping it would duplicate that exact accessible name one level up (DashboardGrid.tsx's own
      // doc comment on this case). Every OTHER section here has a title distinct from its widgets'
      // own titles, so this omission is specific to the one section where title === the lone
      // widget's title.
      id: 'readiness',
      widgets: [
        {
          key: WIDGET_KEY.readiness,
          span: { base: 12, md: 6, lg: 4 },
          minHeight: 200,
          render: () => (
            <WidgetErrorBoundary title="Readiness" icon="check-circle">
              <ReadinessComingSoonWidget filter={filter} skipRefetch={skipRefetchFor(WIDGET_KEY.readiness)} />
            </WidgetErrorBoundary>
          ),
        },
      ],
    },
  ]
}
